"use client";

/**
 * PropertiesDrawer
 * - Shows full PropertiesPanel for the currently selected element
 * - Opens/closes via props
 * - Closes via explicit UI and Escape
 */

import React, { useEffect, useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import PropertiesPanel from "./PropertiesPanel";
import { useEditorStore } from "./editorStore";

const TOP_POPUP_Y = 72;

function findSelected(docData: any, id: string) {
  const pages = Array.isArray(docData?.pages) ? docData.pages : [];
  for (let pageIndex = 0; pageIndex < pages.length; pageIndex += 1) {
    const elements = Array.isArray(pages[pageIndex]?.elements) ? pages[pageIndex].elements : [];
    for (const element of elements) {
      if (element?.id === id) return { el: element, pageIndex };
    }
  }
  return null;
}

export default function PropertiesDrawer({
  isOpen,
  onClose,
  fonts,
  frames,
  formats,
  textPresets,
  onSaveTextPreset
}: {
  isOpen: boolean;
  onClose: () => void;
  fonts: any[];
  frames: any[];
  formats: any[];
  textPresets?: Record<string, any>;
  onSaveTextPreset?: (
    role: "heading" | "body" | "list",
    preset: { style: any; box_mm: { w: number; h: number }; list_marker?: "bullet" | "number" }
  ) => Promise<void> | void;
}) {
  const store = useEditorStore();
  const t = useTranslations("editor.propertiesDrawer");
  const tShape = useTranslations("editor.properties.shape");
  const [drawerEntered, setDrawerEntered] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(false);

  const selectedDoc = store.selected.doc;
  const selectedId = store.selected.elementId;
  const docData = selectedDoc === "content" ? store.contentData : store.coverData;

  const found = useMemo(() => {
    if (!docData || !selectedId) return null;
    return findSelected(docData, selectedId);
  }, [docData, selectedId]);

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobileViewport(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (!isOpen || !found?.el) return;

    setDrawerEntered(false);

    const frame = window.requestAnimationFrame(() => {
      setDrawerEntered(true);
    });

    return () => window.cancelAnimationFrame(frame);
  }, [found?.el?.id, isOpen]);

  if (!isOpen || !found?.el) return null;

  const type = String(found.el.type || "element");
  const textRole = String(found.el.text_role || "body");
  const shapeId = String(found.el.shape_id || "rectangle");
  const isBackground = type === "background" || Boolean(found.el.is_background);
  const shapeLabelMap: Record<string, string> = {
    rectangle: tShape("rectangle"),
    circle: tShape("circle"),
    triangle: tShape("triangle"),
    star: tShape("star"),
    pentagon: tShape("pentagon"),
    octagon: tShape("octagon"),
    rhombus: tShape("rhombus"),
    heart: tShape("heart"),
    crescent: tShape("crescent"),
    kite: tShape("kite"),
    oval: tShape("oval"),
    semicircle: tShape("semicircle"),
  };
  const headerTitle =
    type === "text"
      ? textRole === "heading"
        ? t("heading")
        : textRole === "list"
          ? t("list")
          : t("text")
      : type === "shape"
        ? shapeLabelMap[shapeId] || shapeId
      : isBackground
        ? t("background")
        : t("title", { type });

  return (
    <div className="pointer-events-none fixed inset-0 z-[9999]">
      <div
        className={
          isMobileViewport
            ? `editor-panel pointer-events-auto fixed inset-x-0 bottom-0 z-[10000] max-h-[82dvh] w-full overflow-hidden rounded-t-2xl border-t border-white/10 transition-transform duration-200 ease-out ${
                drawerEntered ? "translate-y-0" : "translate-y-3"
              }`
            : `editor-panel pointer-events-auto fixed z-[10000] h-[calc(100vh-72px)] w-[330px] max-w-[92vw] overflow-hidden border-r border-t-[3px] transition-transform duration-200 ease-out ${
                drawerEntered ? "translate-x-0" : "-translate-x-2"
              }`
        }
        style={isMobileViewport ? undefined : { left: 0, top: TOP_POPUP_Y }}
        onMouseDown={(event) => event.stopPropagation()}
      >
        {isMobileViewport ? (
          <div className="flex justify-center py-2">
            <div className="h-1 w-12 rounded-full bg-white/25" />
          </div>
        ) : null}
        <div className="editor-panel-header flex items-center justify-between px-4 py-5">
          <div className="text-[15px] font-bold uppercase tracking-wide text-white">{headerTitle}</div>
          <button
            type="button"
            className="rounded-md p-1 text-white/85 transition-colors hover:bg-white/5 hover:text-white"
            onClick={onClose}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="m6 6 12 12M18 6 6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className={`${isMobileViewport ? "max-h-[calc(82dvh-78px)]" : "h-[calc(100vh-140px)]"} overflow-auto px-4 py-5`}>
          <PropertiesPanel
            config={{ fonts, frames }}
            formats={formats}
            textPresets={textPresets}
            onSaveTextPreset={onSaveTextPreset}
          />
        </div>
      </div>
    </div>
  );
}
